import { ReportSection } from "~/types/global";
import { REPORT_CONSTANTS } from "~/helpers/constants";

/**
 * Extrai as seções apropriadas de um relatório combinado baseado no filtro selecionado
 */
export function extractCombinadoSections(
  sections: ReportSection[],
  selectedFilter: string
): ReportSection[] {
  const combinadoContainer = sections.find(
    (section: any) => section.combined_data || section.report_sources_data
  );

  if (!combinadoContainer) return sections;

  const showCombinedData = selectedFilter === REPORT_CONSTANTS.combined_data_keys.combined_data;

  if (showCombinedData && Array.isArray((combinadoContainer as any).combined_data)) {
    return (combinadoContainer as any).combined_data;
  } else if (Array.isArray((combinadoContainer as any).report_sources_data)) {
    // Extract report ID from composite value "reportId__index"
    const reportId = selectedFilter.split('__')[0];

    return (combinadoContainer as any).report_sources_data.filter(
      (sourceSection: any) =>
        !!sourceSection && sourceSection.user_reports_id === reportId
    );
  }

  return sections;
}

/**
 * Encontra seções que correspondem aos critérios especificados
 */
export function findMatchingSections(
  sections: ReportSection[],
  sectionTitle: string,
  reportType: string,
  selectedCombinedFilter: string,
  includeSubsections: boolean = false
): ReportSection[] {
  let sectionsToSearch: ReportSection[] = [];

  if (reportType === REPORT_CONSTANTS.types.combinado) {
    sectionsToSearch = extractCombinadoSections(sections, selectedCombinedFilter);
  } else {
    sectionsToSearch = sections;
  }

  return sectionsToSearch.filter((s) => {
    const titleMatches = s.title === sectionTitle;
    const subsectionMatches = includeSubsections || !s.subsection;
    return titleMatches && subsectionMatches;
  });
}

/**
 * Encontra uma seção específica (principal ou subseção)
 */
export function findSection(
  sections: ReportSection[],
  sectionTitle: string,
  reportType: string,
  selectedCombinedFilter: string,
  subsectionName?: string
): ReportSection | null {
  const sectionsToSearch = reportType === REPORT_CONSTANTS.types.combinado
    ? extractCombinadoSections(sections, selectedCombinedFilter)
    : sections;

  return sectionsToSearch.find((s) => {
    const titleMatches = s.title === sectionTitle;
    const subsectionMatches = subsectionName
      ? s.subsection === subsectionName
      : !s.subsection;
    return titleMatches && subsectionMatches;
  }) || null;
}

/**
 * Calcula os registros da lista de seções
 */
export function calculateSectionListRecords(
  sections: ReportSection[],
  reportType: string,
  selectedCombinedFilter: string
) {
  const sectionsToProcess = reportType === REPORT_CONSTANTS.types.combinado
    ? extractCombinadoSections(sections, selectedCombinedFilter)
    : sections;

  const sectionListRecords = sectionsToProcess
    .filter((section: any) => {
      if (section.subsection) return false;
      if (section.is_deleted) return false;
      if (section.data_count > 0) return true;

      return (
        Array.isArray(section.data) &&
        section.data.some(
          (item: any) =>
            Array.isArray(item.detalhes) && item.detalhes.length > 0
        )
      );
    })
    .map((s: any, index: number) => ({
      title: s.title,
      data_count: s.data_count,
      index,
      interface_id: s.interface_id,
    }));

  const totalRecords = sectionListRecords.reduce((sum: number, section: any) => {
    const count = Number(section.data_count) || 0;
    return sum + count;
  }, 0);

  return { sectionListRecords, totalRecords };
}

/**
 * Limpa IDs de seções abertas que não existem mais
 */
export function cleanOpenSections(
  openSections: string[],
  validSectionIds: Set<string>,
  sectionListRecords: any[]
): string[] {
  return openSections.filter((id: string) => {
    return (
      validSectionIds.has(id) ||
      sectionListRecords.some(
        (s: any) => s.title.toLowerCase().replace(/\s+/g, "-") === id
      )
    );
  });
}

/**
 * Valida se uma seção ativa ainda existe
 */
export function isActiveSectionValid(
  activeSection: string | null,
  validSectionIds: Set<string>,
  sectionListRecords: any[]
): boolean {
  if (!activeSection) return false;

  return (
    validSectionIds.has(activeSection) ||
    sectionListRecords.some(
      (s: any) => s.title.toLowerCase().replace(/\s+/g, "-") === activeSection
    )
  );
}
